import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { currencyLabel } from '@/services/networks/currenciesApi'
import { useNetwork } from '../contexts/NetworkContext'

export interface CurrencySelectProps {
  /** ISO currency code, or '' when the field is unset. */
  value: string
  onValueChange: (value: string) => void
  /** Shown (dimmed) when `value` is empty — typically the derived default. */
  fallback?: string
  /**
   * Codes to offer instead of the network's available currencies — for pages
   * that edit the available list themselves and must reflect unsaved state.
   */
  codes?: string[]
  /** Overrides the accessible label; defaults to "Currency". */
  label?: string
  /** Show `NOK – Norwegian Krone` in the list instead of bare codes. */
  showNames?: boolean
  disabled?: boolean
  /** Borderless, compact trigger for use inside an input group addon. */
  inline?: boolean
  className?: string
}

/**
 * Currency picker limited to the network's available currencies.
 * The current value is always offered, even if the network dropped it.
 */
export function CurrencySelect({
  value,
  onValueChange,
  fallback,
  codes: codesOverride,
  label,
  showNames,
  disabled,
  inline,
  className,
}: CurrencySelectProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()

  const available = codesOverride ?? currentNetwork?.availableCurrencies ?? []
  const codes = Array.from(
    new Set(
      [
        ...available,
        ...(codesOverride ? [] : [currentNetwork?.defaultCurrency]),
        fallback,
        value,
      ].filter(Boolean)
    )
  ) as string[]

  const shownCode = value || fallback
  const shown = shownCode && showNames ? currencyLabel(shownCode) : shownCode

  return (
    <Select
      label={label ?? t('common.currency')}
      value={value}
      onValueChange={(v) => onValueChange(v ?? '')}
      disabled={disabled}
    >
      <SelectTrigger
        size={inline ? 'sm' : 'default'}
        className={cn(
          'w-24',
          inline &&
            'h-7 w-auto gap-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0 dark:bg-transparent',
          className
        )}
      >
        <SelectValue placeholder={t('common.currency')}>
          <span className={value ? undefined : 'text-muted-foreground'}>{shown}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {codes.map((code) => (
          <SelectItem key={code} value={code}>
            {showNames ? currencyLabel(code) : code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
